/**
 * useStagedProgress.ts — advances a label sequence on a timer while active.
 *
 * Kept separate from `useSlideTransform` so the animation has no knowledge of
 * the transform pipeline and can be unit-tested on its own.
 */

import { useEffect, useState } from "react";

/**
 * Index of the stage currently being shown: advances every `intervalMs` while
 * `active` is true, then holds on the last stage.
 *
 * The hold is the point. A redesign runs for far longer than the animation, so
 * wrapping or completing early would tell the user the run had finished when it
 * hadn't — only the caller flipping `active` off means "done". The index resets
 * when `active` goes false, so a second run starts from the beginning.
 *
 * @param stageCount number of stages to cycle through; below 1 disables the timer
 * @param intervalMs delay between stages; below 1 disables the timer
 * @param active whether the sequence should be running
 */
export function useStagedProgress(stageCount: number, intervalMs: number, active: boolean): number {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!active) {
      setIndex(0);
      return;
    }
    if (stageCount < 1 || intervalMs < 1) return;

    const lastIndex = stageCount - 1;
    const timer = setInterval(() => {
      // Clamp rather than wrap, and re-clamp on every tick so a shrinking
      // stageCount can't leave the index pointing past the end of the list.
      setIndex((current) => Math.min(current + 1, lastIndex));
    }, intervalMs);

    return () => clearInterval(timer);
  }, [active, intervalMs, stageCount]);

  return index;
}
